import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CrucibleEngine } from '../lib/agents/crucible';
import { sendAnalyticsEvent } from '../lib/analytics';
import { AndroidHead } from './AndroidHead';
import { isTimeoutErrorText } from '../lib/sessionProgress';
import { gambitSoul } from '../config/souls';

type ChatRole = 'user' | 'agent';

type ChatMessage = {
    id: string;
    role: ChatRole;
    text: string;
};

type Props = {
    isOpen: boolean;
    onRequestOpen: () => void;
    onRequestClose: () => void;
};

const STARTER_1 = 'What can I do here?';
const STARTER_2 = 'How do I run a Crucible?';

export const GambitChatWidget: React.FC<Props> = ({ isOpen, onRequestOpen, onRequestClose }) => {
    const navigate = useNavigate();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const launcherRef = useRef<HTMLButtonElement | null>(null);
    const engine = useMemo(() => new CrucibleEngine(), []);
    const [draft, setDraft] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);

    const hasAnyMessages = messages.length > 0;

    useEffect(() => {
        if (!isOpen) return;
        // Let the panel animate in before focusing so focus sticks.
        const t = window.setTimeout(() => {
            inputRef.current?.focus();
        }, 0);
        return () => window.clearTimeout(t);
    }, [isOpen]);

    const append = (role: ChatRole, text: string) => {
        setMessages(prev => [...prev, { id: `${Date.now()}-${Math.random().toString(16).slice(2)}`, role, text }]);
    };

    const send = async (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) return;

        append('user', trimmed);
        setDraft('');
        setIsTyping(true);

        try {
            const result = await engine.processInput(trimmed, 'PARTNER', {
                agentContext: gambitSoul,
            });

            if (result.error && isTimeoutErrorText(String(result.error))) {
                navigate('/', {
                    replace: true,
                    state: {
                        timedOut: true,
                        timeoutReason: String(result.error),
                    },
                });
                return;
            }

            // Requirement: don't fabricate when backend is offline and mock mode is off.
            if (result.error && !result.mockMode) {
                append('agent', `Backend offline (mock mode off). ${result.error}`);
                return;
            }

            append('agent', result.response);
        } catch (e) {
            const err = e instanceof Error ? e.message : String(e);
            append('agent', `Backend offline (mock mode off). ${err}`);
        } finally {
            setIsTyping(false);
        }
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await send(draft);
    };

    const starterButtons = useMemo(() => {
        return (
            <div className="flex flex-col gap-2">
                <button
                    type="button"
                    data-testid="gambit-starter-1"
                    className="text-left text-xs text-slate-200 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 transition-colors"
                    onClick={() => send(STARTER_1)}
                >
                    {STARTER_1}
                </button>
                <button
                    type="button"
                    data-testid="gambit-starter-2"
                    className="text-left text-xs text-slate-200 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 transition-colors"
                    onClick={() => send(STARTER_2)}
                >
                    {STARTER_2}
                </button>
            </div>
        );
    }, []);

    const closeAndRestoreFocus = () => {
        onRequestClose();
        // Restore focus to the launcher for keyboard users.
        window.setTimeout(() => launcherRef.current?.focus(), 0);
    };

    return (
        <div
            data-testid="gambit-widget"
            className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3"
        >
            {/* Panel */}
            {isOpen && (
                <div className="w-[22rem] max-w-[92vw] h-[28rem] max-h-[70vh] bg-black/70 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                        <div className="flex items-center gap-2">
                            <span className="text-purple-400 font-bold uppercase tracking-wider text-xs">Gambit</span>
                            <span className="text-[10px] text-slate-500">sitewide help</span>
                        </div>
                        <button
                            type="button"
                            data-testid="gambit-close"
                            className="text-slate-300 hover:text-white text-xs border border-white/10 hover:border-white/20 rounded-md px-2 py-1 transition-colors"
                            onClick={closeAndRestoreFocus}
                            aria-label="Close Gambit"
                        >
                            Close
                        </button>
                    </div>

                    <div className="px-4 py-3 flex flex-col gap-3 overflow-auto h-[calc(100%-7.5rem)]">
                        {!hasAnyMessages && (
                            <div className="text-xs text-slate-400 leading-relaxed">
                                I'm Gambit — your sitewide help android. Ask me anything about the site, the crew, or how a Crucible works. I'm always listening.
                            </div>
                        )}

                        {starterButtons}

                        {messages.map(m => (
                            <div
                                key={m.id}
                                className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${m.role === 'user'
                                        ? 'self-end bg-purple-600/20 border border-purple-400/20 text-slate-100'
                                        : 'self-start bg-white/5 border border-white/10 text-slate-200'
                                    }`}
                                data-testid={m.role === 'agent' ? 'gambit-response-bubble' : undefined}
                            >
                                {m.text}
                            </div>
                        ))}

                        {isTyping && (
                            <div className="self-start max-w-[85%] rounded-2xl px-3 py-2 text-sm bg-white/5 border border-white/10 text-slate-300">
                                Gambit is thinking…
                            </div>
                        )}
                    </div>

                    <form onSubmit={onSubmit} className="px-4 py-3 border-t border-white/10">
                        <div className="flex gap-2">
                            <input
                                ref={inputRef}
                                data-testid="gambit-input"
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                                placeholder="Ask anything... (e.g., 'How do I navigate to my Canon?')"
                            />
                            <button
                                type="submit"
                                aria-label="Send message"
                                className="bg-purple-500/90 hover:bg-purple-500 text-white text-sm font-semibold rounded-lg px-3 py-2 transition-colors"
                                disabled={isTyping}
                            >
                                Send
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Launcher */}
            <button
                type="button"
                data-testid="gambit-launcher"
                className="group relative flex items-center justify-center w-14 h-14 rounded-full bg-black/40 backdrop-blur-md border border-white/20 shadow-[0_0_20px_rgba(168,85,247,0.3)] transition-all duration-300 hover:scale-110 hover:border-purple-400"
                onClick={() => {
                    if (isOpen) {
                        closeAndRestoreFocus();
                        return;
                    }
                    sendAnalyticsEvent('gambit_open', { source: 'widget' });
                    onRequestOpen();
                }}
                aria-label={isOpen ? 'Close Gambit' : 'Open Gambit'}
                ref={launcherRef}
            >
                <span className="absolute inset-0 rounded-full bg-purple-500 opacity-20 animate-pulse" />
                <div className="w-10 h-10">
                    <AndroidHead className="w-full h-full" color="text-purple-400" mood="talking" />
                </div>
            </button>
        </div>
    );
};
